import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  mergeMap,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { AnalysisResultsService } from '../services/analysis-results.service';
import * as AnalysisActions from '../actions/analysis.actions';
import { StrideReviewActions } from '../../stride-review-store';
import {
  AnalysisResults,
  ErrorStore,
  ErrorStoreStatusEnum,
} from '@sleipai/sleip-models';
import { Destroy } from '../../common-store/services/destroy.service';

@Injectable()
export class AnalysisEffects {
  loadAnalysiss$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AnalysisActions.loadAnalysis),
      concatMap(() =>
        /** An EMPTY observable only emits completion. Replace with your own observable API request */
        EMPTY.pipe(
          map((data) => AnalysisActions.loadAnalysisSuccess({ data })),
          catchError((error) =>
            of(AnalysisActions.loadAnalysisFailure({ error }))
          )
        )
      )
    );
  });

  loadSingleAnalyis$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AnalysisActions.loadAnalysisById),
      map((actions) => actions),
      mergeMap(({ id, isFlexion }) => {
        return this.analysisResultsService.loadSingleAnalysisById(id, isFlexion).pipe(
          switchMap((data) => {
            const analysis = data as AnalysisResults;
            return [
              AnalysisActions.addSingleAnalysis({ analysis: analysis }),
              ...(analysis?.reviewDataBlob ? [StrideReviewActions.loadSingleStrideReview({
                recordingId: id,
                reviewDataBlob: analysis.reviewDataBlob,
              })] : [])
            ];
          })
        );
      }),
      catchError((err) => {
        console.log(err);
        return EMPTY;
      })
    );
  });

  loadMultipleAnalyisByIds$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AnalysisActions.loadMultipleAnalysisResultsByIds),
      map((actions) => actions),
      mergeMap(({ recordingIds }) => {
        return this.analysisResultsService
          .loadMultipleAnalysisResultsByIds(recordingIds)
          .pipe(
            takeUntil(this.destroy$.destroySubject$),
            switchMap((analysisResults) => {
              return [
                AnalysisActions.loadMultipleAnalysisResultsByIdsSuccess({
                  analysisResults,
                }),
              ];
            })
          );
      }),
      catchError((error) => {
        const errorStore: ErrorStore = {
          message: error?.message ?? error,
          status: ErrorStoreStatusEnum.INTERNAL,
        };
        return of(
          AnalysisActions.loadAnalysisFailure({
            error: errorStore,
          })
        );
      })
    );
  });

  shareAnalysisResult$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AnalysisActions.shareAnalysis),
      map((actions) => actions),
      mergeMap(({ horseId, recordingIds, version }) => {
        return this.analysisResultsService
          .generateShardAnalysisResultLink(horseId, recordingIds, version)
          .pipe(
            takeUntil(this.actions$.pipe(ofType(AnalysisActions.cancelShareAnalysis))),
            takeUntil(this.destroy$.destroySubject$),
            switchMap((data) => {
              return [
                AnalysisActions.shareAnalysisSuccess({ data }),
                AnalysisActions.resetShareAnalysisLink(),
              ];
            }),
            catchError((error) => {
              return [
                AnalysisActions.shareAnalysisFailure({
                  error: error.message ?? error,
                }),
              ];
            })
          );
      })
    );
  });

  generateAnalysisResultPDF$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AnalysisActions.generateAnalysisResultPDF),
      map((actions) => actions),
      mergeMap(({ horseId, recordingIds, userType, timeZone }) => {
        return this.analysisResultsService
          .generateAnalysisResultPDF(horseId, recordingIds, userType, timeZone)
          .pipe(
            takeUntil(this.actions$.pipe(ofType(AnalysisActions.cancelShareAnalysis))),
            takeUntil(this.destroy$.destroySubject$),
            switchMap((data) => {
              const { downloadUrl } = data as { downloadUrl: string };
              return [
                AnalysisActions.generateAnalysisResultPDFSuccess({
                  downloadUrl,
                }),
                AnalysisActions.resetShareAnalysisLink(),
              ];
            }),
            catchError((error) => {
              return [
                AnalysisActions.shareAnalysisFailure({
                  error: error.message ?? error,
                }),
              ];
            })
          );
      })
    );
  });

  resetLoadingFlag$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AnalysisActions.enableAnalysisLoadingFlag),
      concatMap(() => of(AnalysisActions.resetAnalysisLoadingFlag())),
      catchError((err) => {
        console.log(err);
        return EMPTY;
      })
    );
  });

  constructor(
    private actions$: Actions,
    private analysisResultsService: AnalysisResultsService,
    private readonly destroy$: Destroy
  ) { }
}
